
export interface PerigonArticle {
  url: string;
  title: string;
  description: string;
  content?: string;
  summary?: string;
  imageUrl?: string;
  authorsByline?: string;
  articleId: string;
  clusterId?: string;
  source: {
    domain: string;
    location?: {
      country: string;
      state?: string;
      city?: string;
      coordinates?: {
        lat: number;
        lon: number;
      };
    };
  };
  country: string;
  language: string;
  pubDate: string;
  addDate?: string;
  refreshDate?: string;
  score?: number;
  medium?: string;
  links?: string[];
  labels?: Array<{ name: string }>;
}

export interface PerigonResponse {
  status: number;
  numResults: number;
  summary: string;
  results: PerigonArticle[];
}

export interface NewsAgentResponse {
  articles: PerigonArticle[];
  summary: string;
  numResults: number;
  query: string;
  error?: string;
}

// Simple Perigon News Agent using basic articles endpoint
export const newsAgent = async (userQuery: string): Promise<NewsAgentResponse> => {
  try {
    const apiKey = 'f5d0adbc-560f-4c6d-bd40-bb9df8efae9d';

    // Check if user provided a specific date, otherwise use today's date
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
    const hasDateInQuery = /\b(today|yesterday|\d{4}-\d{2}-\d{2}|\d{1,2}\/\d{1,2}\/\d{4}|\d{1,2}-\d{1,2}-\d{4})\b/i.test(userQuery);

    // Use basic Perigon Articles API (GET request)
    const searchQuery = encodeURIComponent(userQuery);
    const dateFilter = hasDateInQuery ? '' : `&from=${today}&to=${today}`;
    const url = `https://api.perigon.io/v1/all?apiKey=${apiKey}&q=${searchQuery}&language=en&size=10&excludeLabel=Non-news&showReprints=false${dateFilter}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Perigon API Error:', response.status, errorText);
      throw new Error(`Perigon API returned ${response.status}: ${errorText}`);
    }

    const data = await response.json();

    // Create a simple summary from the first few articles
    const articles = data.articles || [];
    const summary = articles.length > 0
      ? `Found ${articles.length} recent articles about "${userQuery}". Latest news includes coverage from ${articles.slice(0, 3).map(a => a.source?.name || 'various sources').join(', ')}.`
      : `No recent articles found for "${userQuery}".`;

    return {
      articles: articles,
      summary: summary,
      numResults: data.numResults || articles.length,
      query: userQuery
    };

  } catch (error) {
    console.error('Perigon News Agent Error:', error);
    return {
      articles: [],
      summary: `Unable to fetch news for "${userQuery}" - ${error instanceof Error ? error.message : 'Unknown error'}`,
      numResults: 0,
      query: userQuery,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
};

// Legacy exports for backward compatibility
export const newsService = newsAgent;

